import { Injectable } from '@angular/core';
import { isDefined } from '@fi-shared/utils/is-defined';
import { TextEditorSelectionElementBuilderService } from './text-editor-selection-element-builder.service';
import { findClosest } from '@fi-shared/utils';

interface ITextEditorChangesHistory {
    undoHistory: string[];
    redoHistory: string[];
    element: HTMLElement;
}

@Injectable()
export class TextEditorHistoryService {
    private uid: number = 0;
    private readonly textEditorIdName: string = 'text-editor-';

    getContentEditableElementUid(): string {
        return `${this.textEditorIdName}${++this.uid}`;
    }

    private changesHistoryData: ITextEditorChangesHistory[] = [];
    private currentChangeHistoryDataIndex: number;
    private readonly textEditorClassSelector: string = '.text-editor';

    constructor(private textEditorSelectionElementBuilder: TextEditorSelectionElementBuilderService) {}

    setElement(element: HTMLElement) {
        this.currentChangeHistoryDataIndex = this.changesHistoryData.findIndex(
            (history: ITextEditorChangesHistory): boolean => {
                return history.element && history.element.id === element.id;
            },
        );

        if (this.currentChangeHistoryDataIndex === -1) {
            this.addNewChangesHistoryData(element);
            this.currentChangeHistoryDataIndex = this.changesHistoryData.length - 1;
            this.changesHistoryData[this.currentChangeHistoryDataIndex].undoHistory.push(element.innerHTML);
        }
    }

    getElement(): HTMLElement {
        const currentRange: Range = this.textEditorSelectionElementBuilder.getCurrentRange();
        if (isDefined(currentRange)) {
            const closestTextEditor: HTMLElement = findClosest(
                this.textEditorSelectionElementBuilder.getCurrentRange().commonAncestorContainer as HTMLElement,
                this.textEditorClassSelector,
            );
            if (isDefined(closestTextEditor)) {
                this.setElement(closestTextEditor);
            }
        }
        return this.getCurrentHistoryData().element;
    }

    saveState(content?: string, editableElementId?: string) {
        let currentHistoryData: ITextEditorChangesHistory = isDefined(editableElementId)
            ? this.getHistoryDataByElementId(editableElementId)
            : this.getCurrentHistoryData();

        if (!isDefined(currentHistoryData)) {
            this.addNewChangesHistoryData(document.querySelector(`#${editableElementId}`));
            currentHistoryData = this.changesHistoryData[this.changesHistoryData.length - 1];
        }

        if (!isDefined(content)) content = currentHistoryData.element ? currentHistoryData.element.innerHTML : '';

        if (this.isNewContent(content, currentHistoryData)) currentHistoryData.undoHistory.push(content);
    }

    private isNewContent(content: string, currentHistoryData: ITextEditorChangesHistory): boolean {
        const result =
            (!currentHistoryData.undoHistory.includes(content) ||
                currentHistoryData.undoHistory.indexOf(content) !== currentHistoryData.undoHistory.length - 1) &&
            currentHistoryData.redoHistory[currentHistoryData.redoHistory.length - 1] !== content;
        return result;
    }

    undo(): string {
        const currentHistoryItem: ITextEditorChangesHistory = this.getCurrentHistoryData();
        const state: string = currentHistoryItem.undoHistory.pop();
        if (isDefined(state)) {
            currentHistoryItem.redoHistory.push(currentHistoryItem.element.innerHTML);
            currentHistoryItem.element.innerHTML = state;
        }
        return state;
    }

    redo(): string {
        const currentHistoryItem: ITextEditorChangesHistory = this.getCurrentHistoryData();
        const state: string = currentHistoryItem.redoHistory.pop();
        if (isDefined(state)) {
            currentHistoryItem.undoHistory.push(currentHistoryItem.element.innerHTML);
            currentHistoryItem.element.innerHTML = state;
        }
        return state;
    }

    restoreFocus() {
        const selection: Selection = window.getSelection();
        if (selection.rangeCount) {
            const range: Range = selection.getRangeAt(0);
            const textEditorElem = findClosest(range.startContainer as HTMLElement, this.textEditorClassSelector);
            if (textEditorElem) {
                textEditorElem.focus();
            }
        }
    }

    resetChangesHistoryData() {
        this.changesHistoryData = [];
    }

    private getCurrentHistoryData(): ITextEditorChangesHistory {
        return this.changesHistoryData[this.currentChangeHistoryDataIndex];
    }

    private getHistoryDataByElementId(editableElementId: string): ITextEditorChangesHistory {
        return this.changesHistoryData.find((historyData) => {
            const result = historyData.element && historyData.element.id === editableElementId;
            return result;
        });
    }

    private addNewChangesHistoryData(element: HTMLElement) {
        this.changesHistoryData.push({
            undoHistory: [],
            redoHistory: [],
            element: element,
        });
    }
}
